<script>
    import { Pagination , InfoBlock, Select, Button } from "paxray-components-svelte";
  import EditIcon from "../../settings/svg/EditIcon.svelte";
  import RemoveIcon from "../../settings/svg/RemoveIcon.svelte";
  import PlusIcon from "@components/common/icons/app/PlusIcon.svelte";
  import { PLUS_ICON, RESET_ICON } from '@utils/icons.js';

  export let listOfOrgBusinessUnits = [];
  export let listOfOrgDepartments = [];
  export let listOfOrgAreas = [];
  export let selectedOrgBusinessUnit = "";
  export let selectedOrgDepartment = "";
  export let selectedOrgArea = "";
  export let orgFilterKey = 0;
  export let orgFrameworkLoading = false;
  export let orgFrameworkError = "";
  export let orgFrameworkPagedRows = [];
  export let visibleOrgFrameworkRows = [];
  export let orgFrameworkPage = 1;
  export let orgFrameworkPageSize = 25;
  export let resetOrgFilters = () => {};
  export let openOrgAddModal = () => {};
  export let toggleOrgBusinessUnit = () => {};
  export let openOrgEditModal = () => {};
  export let openOrgDeleteModal = () => {};

  const columns = [
    { key: "businessUnit", name: "Business Unit", type: "Business-Units" },
    { key: "department", name: "Department", type: "Departments" },
    { key: "area", name: "Area", type: "Areas" },
  ];
  $: businessUnitKeys = [
    ...new Set(visibleOrgFrameworkRows.map((row) => row.businessUnitKey)),
  ];
  $: alternateBusinessUnitKeys = new Set(
    businessUnitKeys.filter((key, index) => index % 2 === 1),
  );
  $: businessUnitSelectItems = listOfOrgBusinessUnits.map((option) => ({
    value: option.key,
    text: option.value,
  }));
  $: departmentSelectItems = listOfOrgDepartments.map((option) => ({
    value: option.key,
    text: option.value,
  }));
  $: areaSelectItems = listOfOrgAreas.map((option) => ({
    value: option.key,
    text: option.value,
  }));
</script>

<div class="company-framework-content">
  <div class="company-framework-intro">
    <InfoBlock
      type="info"
      heading="Orga Framework"
      showClose={true}
      message="Define your company's organizational structure across business units, departments and areas."
    />
  </div>
  <div class="company-framework-filter-bar">
    {#key orgFilterKey}
      <Select
        items={businessUnitSelectItems}
        labelText="Business Units"
        bind:selected={selectedOrgBusinessUnit}
        color="dark"
      />
      <Select
        items={departmentSelectItems}
        labelText="Departments"
        bind:selected={selectedOrgDepartment}
        color="dark"
      />
      <Select
        items={areaSelectItems}
        labelText="Areas"
        bind:selected={selectedOrgArea}
        color="dark"
      />
    {/key}
    <Button
      size="small"
      kind="secondary"
      icon={RESET_ICON}
      iconDescription="Reset filters"
      aria-label="Reset filters"
      on:click={resetOrgFilters}
    />
  </div>

  <div class="company-framework-table-wrapper">
    <table class="company-framework-table org-framework-table">
      <thead
        ><tr>
          <th class="company-framework-expand-heading"></th>
          {#each columns as column}
            <th scope="col"
              ><div class="company-framework-heading">
                <span>{column.name}</span>
                  <Button
                  icon={PLUS_ICON}
                  iconDescription={`Add ${column.name.toLowerCase()}`}
                  aria-label={`Add ${column.name.toLowerCase()}`}
                  on:click={() => openOrgAddModal(column.type)}
                />
              </div></th
            >
          {/each}
        </tr></thead
      >
      <tbody>
        {#if orgFrameworkLoading}
          <tr
            ><td class="company-framework-empty" colspan="4"
              >Loading organizational structure...</td
            ></tr
          >
        {:else if orgFrameworkError}
          <tr
            ><td class="company-framework-empty" colspan="4"
              >{orgFrameworkError}</td
            ></tr
          >
        {:else if orgFrameworkPagedRows.length}
          {#each orgFrameworkPagedRows as row, rowIndex (row.id)}
            {#if row.isBusinessUnitStart || rowIndex === 0}
              <tr
                class="company-framework-group"
                class:alternate-group={alternateBusinessUnitKeys.has(
                  row.businessUnitKey,
                )}
              >
                <td class="company-framework-expand-cell">
                  <button
                    type="button"
                    class="company-framework-expand-button"
                    aria-expanded={!row.isBusinessUnitCollapsed}
                    aria-label={`${row.isBusinessUnitCollapsed ? "Expand" : "Collapse"} ${row.businessUnit?.name || "Unassigned business unit"}`}
                    on:click={() => toggleOrgBusinessUnit(row.businessUnitKey)}
                    ><span
                      class:collapsed={row.isBusinessUnitCollapsed}
                      class="tree-chevron"
                      aria-hidden="true"
                    ></span></button
                  >
                </td>
                <td colspan="3"
                  ><button
                    type="button"
                    class="company-framework-group-name"
                    aria-expanded={!row.isBusinessUnitCollapsed}
                    on:click={() => toggleOrgBusinessUnit(row.businessUnitKey)}
                    >{row.businessUnit?.name ||
                      "Unassigned business unit"}</button
                  ></td
                >
              </tr>
            {/if}
            {#if !row.isBusinessUnitCollapsed}
              <tr
                class="company-framework-detail-row"
                class:alternate-group={alternateBusinessUnitKeys.has(
                  row.businessUnitKey,
                )}
              >
                <td class="company-framework-expand-cell"></td>
                {#each columns as column}
                  <td>
                    {#if row[column.key]}
                      <div class="company-framework-cell">
                        <span
                          class="company-framework-cell-name"
                          title={row[column.key].name}
                          >{row[column.key].name}</span
                        >
                        <div class="company-framework-cell-actions">
                          <button
                            type="button"
                            class="company-framework-action-button"
                            title={`Edit ${column.name}: ${row[column.key].name}`}
                            aria-label={`Edit ${column.name}: ${row[column.key].name}`}
                            on:click={() => openOrgEditModal(column.type, row)}
                            ><EditIcon /></button
                          >
                          <button
                            type="button"
                            class="company-framework-action-button"
                            title={`Delete ${column.name}: ${row[column.key].name}`}
                            aria-label={`Delete ${column.name}: ${row[column.key].name}`}
                            on:click={() =>
                              openOrgDeleteModal(column.type, row)}
                            ><RemoveIcon /></button
                          >
                        </div>
                      </div>
                    {:else}
                      <span class="company-framework-missing"
                        >No {column.name}</span
                      >
                    {/if}
                  </td>
                {/each}
              </tr>
            {/if}
          {/each}
        {:else}
          <tr
            ><td class="company-framework-empty" colspan="4"
              >No organizational structure exists yet.</td
            ></tr
          >
        {/if}
      </tbody>
    </table>
    <div class="company-framework-pagination">
      <Pagination
        dark
        bind:pageSize={orgFrameworkPageSize}
        bind:page={orgFrameworkPage}
        totalItems={visibleOrgFrameworkRows.length}
        pageSizes={[10, 25, 50, 75, 100]}
      />
    </div>
  </div>
</div>

<style>
  .company-framework-content {
    display: flex;
    width: 100%;
    min-height: calc(100vh - 4rem);
    flex: 1;
    flex-direction: column;
    gap: var(--control-gap);
    background: var(--pax-white);
  }
  .company-framework-filter-bar {
    display: flex;
    flex-wrap: wrap;
    min-height: 2.5rem;
    box-sizing: border-box;
    align-items: center;
    gap: var(--control-gap);
    padding: 0 1rem;
  }
  .company-framework-filter-bar :global(.bx--form-item) {
    flex: 1 1 8rem;
    min-width: 0;
  }

  .company-framework-table-wrapper {
    overflow: auto;
    margin: 0 1rem;
    border: 1px solid var(--pax-smoke);
    border-radius: 0.5rem;
    background: var(--pax-white);
  }
  .company-framework-table {
    width: 100%;
    min-width: 42rem;
    table-layout: fixed;
    font-size: 0.8125rem;
    border-right: 1px solid var(--border-color);
    border-left: 1px solid var(--border-color);
    border-collapse: collapse;
  }
  .company-framework-table th,
  .company-framework-table td {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid var(--pax-smoke);
    color: var(--pax-black);
    text-align: left;
    vertical-align: middle;
  }
  .company-framework-table th {
    font-size: 0.875rem;
    font-weight: 400;
    background-color: var(--pax-black);
    color: var(--pax-white);
  }
  .company-framework-intro {
    margin: var(--control-gap) 1rem 0;
  }
  .company-framework-intro:empty {
    display: none;
  }
  .company-framework-intro :global(.warning-notice) {
    margin-bottom: 0;
  }
  .company-framework-intro:empty + .company-framework-filter-bar {
    margin-top: var(--control-gap);
  }

  .company-framework-heading {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  .company-framework-table tbody td {
    background-color: var(--pax-white);
  }
  .company-framework-table tbody tr.alternate-group td {
    background-color: color-mix(
      in srgb,
      var(--pax-smoke) 70%,
      var(--pax-white) 30%
    ) !important;
  }
  .company-framework-table tbody tr.company-framework-detail-row:hover td {
    background-color: var(--pax-smoke) !important;
  }
  .company-framework-group-name {
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
    padding: 0;
    border: 0;
    background: transparent;
    color: var(--pax-black);
    font: inherit;
    font-weight: 600;
    text-align: left;
    text-overflow: ellipsis;
    white-space: nowrap;
    cursor: pointer;
  }
  .company-framework-group-name:focus-visible {
    outline: 2px solid var(--pax-black);
    outline-offset: 0.125rem;
  }
  .company-framework-table tbody tr.company-framework-group td,
  .company-framework-table tbody tr.company-framework-detail-row td {
    border-bottom-color: var(--border-color, #d9d9d9);
  }
  .company-framework-cell {
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 0.5rem;
  }
  .company-framework-cell-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .company-framework-cell-actions {
    display: flex;
    gap: 0.25rem;
  }
  @media (hover: hover) {
    .company-framework-cell {
      gap: 0;
    }
    .company-framework-cell-actions {
      max-height: 0;
      margin-top: 0;
      overflow: hidden;
      opacity: 0;
      pointer-events: none;
      transform: translateY(-0.25rem);
      transition:
        max-height 150ms ease,
        margin-top 150ms ease,
        opacity 150ms ease,
        transform 150ms ease;
    }
    .company-framework-detail-row:hover .company-framework-cell-actions,
    .company-framework-cell:focus-within .company-framework-cell-actions {
      max-height: 2rem;
      margin-top: 0.5rem;
      opacity: 1;
      pointer-events: auto;
      transform: translateY(0);
    }
  }
  .company-framework-expand-button:focus-visible,
  .company-framework-action-button:focus-visible {
    outline: 2px solid var(--pax-black);
    outline-offset: 0.125rem;
  }
  .company-framework-table thead button:focus-visible {
    outline-color: var(--pax-white);
  }
  .company-framework-expand-heading,
  .company-framework-expand-cell {
    width: 3.5rem;
    padding: 0.5rem !important;
    text-align: center !important;
  }
  .company-framework-expand-button,
  .company-framework-action-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 0;
    background: transparent;
    cursor: pointer;
  }
  .company-framework-expand-button {
    width: 1.75rem;
    height: 1.75rem;
  }
  .tree-chevron {
    display: block;
    width: 0.45rem;
    height: 0.45rem;
    border-right: 1.5px solid currentColor;
    border-bottom: 1.5px solid currentColor;
    transform: rotate(45deg);
  }
  .tree-chevron.collapsed {
    transform: rotate(-45deg);
  }
  .company-framework-empty {
    color: var(--pax-sub) !important;
    text-align: center !important;
  }
  .company-framework-action-button {
    width: 2rem;
    height: 2rem;
    vertical-align: middle;
    border-radius: 0.35rem;
    background: var(--pax-black);
  }
  .company-framework-pagination {
    overflow: hidden;
  }
  .company-framework-pagination :global(.bx--pagination) {
    box-sizing: border-box;
    width: 100%;
    min-height: 2.5rem;
    border: 0;
    border-top: 1px solid var(--pax-sub-new);
    background: var(--pax-black);
    color: var(--pax-white);
  }
  .company-framework-pagination
    :global(.bx--pagination__left > label.bx--pagination__text) {
    margin-right: 0;
    padding-right: var(--control-gap);
  }
  .company-framework-pagination
    :global(.bx--select__page-number + .bx--pagination__text) {
    margin-left: 0;
    padding-left: var(--control-gap);
  }
  .company-framework-pagination :global(.bx--pagination__text) {
    color: var(--pax-white);
  }
  .company-framework-pagination :global(.bx--select-input) {
    border-color: var(--pax-sub-new) !important;
    background: var(--pax-black);
    color: var(--pax-white);
  }
  .company-framework-pagination :global(.bx--select-input:hover),
  .company-framework-pagination :global(.bx--select-input:focus) {
    background: var(--pax-sub-new) !important;
  }
  .company-framework-pagination :global(.bx--select__arrow) {
    fill: var(--pax-white);
  }
  .company-framework-pagination :global(.bx--pagination__button) {
    border-left-color: var(--pax-sub-new);
    color: var(--pax-white);
  }
  .company-framework-pagination :global(.bx--pagination__button .bx--btn__icon),
  .company-framework-pagination
    :global(.bx--pagination__button .bx--btn__icon path) {
    fill: currentColor !important;
  }
  .company-framework-pagination :global(.bx--pagination__button:hover) {
    background: var(--pax-sub-new);
    color: var(--pax-white);
  }
  .company-framework-pagination :global(.bx--pagination__button:disabled) {
    background: var(--pax-black);
    color: var(--pax-grey);
  }
  .company-framework-missing {
    display: inline-flex;
    padding: 0.125rem 0.5rem;
    border-radius: 1rem;
    background: var(--pax-red);
    font-size: 0.75rem;
  }
</style>
